import type { BundledMap } from '@yimoka/i18n';

import { createTenantWebI18n } from './create-i18n';

import { DEFAULT_LANGUAGE, getCurrentLanguage } from './language';

import { bundledLocales } from './index';

type TenantWebI18n = ReturnType<typeof createTenantWebI18n<typeof bundledLocales>>;

const GLOBAL_I18N_KEY = '__qiyun_tenant_web_i18n__';
const globalRegistry = globalThis as unknown as Record<string, TenantWebI18n | undefined>;
const tenantWebI18n = globalRegistry[GLOBAL_I18N_KEY]
  ?? createTenantWebI18n(bundledLocales, 'tenant-web:i18n:');

globalRegistry[GLOBAL_I18N_KEY] = tenantWebI18n;

const fallbackLocaleResources: Record<string, Record<string, unknown>> = {};

/** mergeLocaleObject 深合并同一命名空间的分包语言资源，供初始化前安全读取。 */
function mergeLocaleObject(
  target: Record<string, unknown>,
  source: Record<string, unknown>,
): void {
  Object.entries(source).forEach(([key, value]) => {
    if (
      value
      && typeof value === 'object'
      && !Array.isArray(value)
    ) {
      const current = target[key];
      const next = current && typeof current === 'object' && !Array.isArray(current)
        ? current as Record<string, unknown>
        : {};
      mergeLocaleObject(next, value as Record<string, unknown>);
      Object.assign(target, { [key]: next });
      return;
    }
    Object.assign(target, { [key]: value });
  });
}

/** registerFallbackLocaleResources 登记本地语言包，让纯 Biz/工具函数不依赖 React 根组件初始化时序。 */
export function registerFallbackLocaleResources(resources: BundledMap): void {
  Object.entries(resources).forEach(([language, namespaces]) => {
    const languageResources = fallbackLocaleResources[language] ?? {};
    Object.entries(namespaces).forEach(([namespace, bundle]) => {
      const namespaceResources = languageResources[namespace];
      const target = namespaceResources && typeof namespaceResources === 'object'
        ? namespaceResources as Record<string, unknown>
        : {};
      mergeLocaleObject(target, bundle as Record<string, unknown>);
      languageResources[namespace] = target;
    });
    fallbackLocaleResources[language] = languageResources;
  });
}

registerFallbackLocaleResources(bundledLocales);

/** resolveFallbackTranslation 从已登记本地包解析命名空间键，并执行最小变量插值。 */
function resolveFallbackTranslation(
  key: string,
  options?: Record<string, unknown>,
): string | undefined {
  const [namespace, path = ''] = key.split(':', 2);
  const languageResources = fallbackLocaleResources[getCurrentLanguage()]
    ?? fallbackLocaleResources[DEFAULT_LANGUAGE];
  let value: unknown = languageResources?.[namespace];
  for (const segment of path.split('.').filter(Boolean)) {
    if (!value || typeof value !== 'object') return undefined;
    value = (value as Record<string, unknown>)[segment];
  }
  if (typeof value !== 'string') return undefined;
  return value.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (matched, name: string) => {
    const replacement = options?.[name];
    return replacement === undefined || replacement === null
      ? matched
      : String(replacement);
  });
}

// federation host 与 remote 都从全局注册表读取同一个实例；模块仅增量注入资源，
// 不得因为构建边界再次创建 i18n，从而导致 remote 在 useTranslation 处永久挂起。
export const {
  i18n,
  I18nRoot,
  useTranslation,
  useSchemaI18nKey,
} = tenantWebI18n;

/** t 优先读取已初始化运行时；初始化前回退本地包，确保首屏 Biz 反馈和纯函数始终有文案。 */
export function t(key: string, options?: Record<string, unknown>): string {
  if (i18n.isInitialized) {
    const translated = tenantWebI18n.t(key, options);
    if (translated && translated !== key) return translated;
  }
  return resolveFallbackTranslation(key, options) ?? key;
}
